/**
 * @file
 * The carousel class.
 */

import { addClass, removeClass } from "../domHelpers.js";
import { preventEvent, keyPress } from "../eventHandlers.js";
import { isTag, isValidClassList, isValidType } from "../validate.js";
import Component from "../Component.js";

class Carousel extends Component {
  /**
   * The DOM elements within the carousel.
   *
   * @protected
   *
   * @type {Object<HTMLElement, HTMLElement[]>}
   *
   * @property {HTMLElement}   carousel                 - The carousel element.
   * @property {HTMLElement[]} carouselItems            - The carousel items.
   * @property {HTMLElement}   carouselItemContainer    - The carousel item container.
   * @property {HTMLElement[]} carouselControls         - The carousel controls.
   * @property {HTMLElement}   carouselControlContainer - The carousel control container.
   * @property {HTMLElement[]} carouselTabs             - The carousel tabs.
   * @property {HTMLElement}   carouselTabContainer     - The carousel tab container.
   * @property {HTMLElement}   autoplay                 - The autoplay button.
   * @property {HTMLElement}   next                     - The next button.
   * @property {HTMLElement}   previous                 - The previous button.
   */
  _dom = {
    carousel: null,
    carouselItems: [],
    carouselItemContainer: null,
    carouselControls: [],
    carouselControlContainer: null,
    carouselTabs: [],
    carouselTabContainer: null,
    autoplay: null,
    next: null,
    previous: null,
  };

  /**
   * The DOM elements within the carousel that cannot be reset or generated by the carousel itself.
   *
   * @protected
   *
   * @type {string[]}
   */
  _protectedDOMElements = ["carousel"];

  /**
   * The query selectors used by the carousel.
   *
   * @protected
   *
   * @type {Object<string>}
   *
   * @property {string} carouselItems            - The query selector string for carousel items.
   * @property {string} carouselItemContainer    - The query selector string for the carousel item container.
   * @property {string} carouselControls         - The query selector string for carousel controls.
   * @property {string} carouselControlContainer - The query selector string for carousel control container.
   * @property {string} carouselTabs             - The query selector string for the carousel tabs.
   * @property {string} carouselTabContainer     - The query selector string for the carousel tab container.
   * @property {string} autoplay                 - The query selector string for the autoplay button.
   * @property {string} next                     - The query selector string for the next button.
   * @property {string} previous                 - The query selector string for the previous button.
   */
  _selectors = {
    carouselItems: "",
    carouselItemContainer: "",
    carouselControls: "",
    carouselControlContainer: "",
    carouselTabs: "",
    carouselTabContainer: "",
    autoplay: "",
    next: "",
    previous: "",
  };

  /**
   * The CSS classes to apply when the component is in various states.
   *
   * @protected
   *
   * @type {Object<string, string[]>}
   *
   * @property {string|string[]} active     - The class(es) to apply when a carousel item is active.
   * @property {string|string[]} previous   - The class(es) to apply to a carousel item that is the previously active item.
   * @property {string|string[]} next       - The class(es) to apply to a carousel item that is the next active item.
   * @property {string|string[]} play       - The class(es) to apply to the autoplay button when the carousel is paused.
   * @property {string|string[]} pause      - The class(es) to apply to the autoplay button when the carousel is playing.
   * @property {string|string[]} initialize - The class(es) to apply when the carousel is initializing.
   */
  _classes = {
    active: "active",
    previous: "previous",
    next: "next",
    play: "play",
    pause: "pause",
    initialize: "initializing",
  };

  /**
   * The duration times (in milliseconds) for variouse aspects throughout the carousel.
   *
   * @protected
   *
   * @type {Object<number>}
   *
   * @property {number} transition - The duration time (in milliseconds) for the transition between carousel items.
   */
  _durations = {
    transition: 250,
  };

  /**
   * The delay times (in milliseconds) for various aspects throughout the component.
   *
   * @protected
   *
   * @type {Object<number>}
   *
   * @property {number} transition = The delay time (in milliseconds) for the transition between carousel items.
   */
  _delays = {
    transition: 10000,
  };

  /**
   * The index of the currently active carousel item.
   *
   * @protected
   *
   * @type {number}
   */
  _currentItem = 0;

  /**
   * A flag to indicate if the carousel is currently playing.
   *
   * @protected
   *
   * @type {boolean}
   */
  _autoplay = true;

  /**
   * The label for the autoplay button when the carousel is paused.
   *
   * @protected
   *
   * @type {string}
   */
  _playText = "Play";

  /**
   * The label for the autoplay button when the carousel is playing.
   *
   * @protected
   *
   * @type {string}
   */
  _pauseText = "Pause";

  /**
   * The current action being performed by the carousel.
   *
   * @protected
   *
   * @type {string}
   */
  _currentAction = "next";

  /**
   * The key used for storage.
   *
   * @protected
   *
   * @type {string}
   */
  _storageKey = "carousels";

  /**
   * Constructs a new `Carousel`.
   *
   * @param {object}             options                                                                  - The options for the generated carousel.
   * @param {HTMLElement}        options.carouselElement                                                  - The carousel element in the DOM.
   * @param {string}             [options.carouselItemSelector = .carousel-item]                          - The query selector string for carousel items.
   * @param {string}             [options.carouselItemContainerSelector = .carousel-item-container]       - The query selector string for the carousel item container.
   * @param {string}             [options.carouselControlSelector = .carousel-control]                    - The query selector string for carousel controls.
   * @param {string}             [options.carouselControlContainerSelector = .carousel-control-container] - The query selector string for carousel control container.
   * @param {string}             [options.carouselTabSelector = .carousel-tab]                            - The query selector string for carousel tabs.
   * @param {string}             [options.carouselTabContainerSelector = .carousel-tab-container]         - The query selector string for the carousel tab container.
   * @param {string}             [options.autoplaySelector = .autoplay]                                   - The query selector string for the autoplay button.
   * @param {string}             [options.nextSelector = .next]                                           - The query selector string for the next button.
   * @param {string}             [options.previousSelector = .previous]                                   - The query selector string for the previous button.
   * @param {?(string|string[])} [options.activeClass = active]                                           - The class(es) to apply when a carousel item is active.
   * @param {?(string|string[])} [options.previousClass = previous]                                       - The class(es) to apply to a carousel item that is the previously active item.
   * @param {?(string|string[])} [options.nextClass = next]                                               - The class(es) to apply to a carousel item that is the next active item.
   * @param {?(string|string[])} [options.playClass = play]                                               - The class(es) to apply to the autoplay button when the carousel is paused.
   * @param {?(string|string[])} [options.pauseClass = pause]                                             - The class(es) to apply to the autoplay button when the carousel is playing.
   * @param {boolean}            [options.autoplay = true]                                                - A flag to indicate if the carousel should autoplay.
   * @param {number}             [options.transitionDelay = 10000]                                        - A flag to initialize the carousel immediately upon creation.
   * @param {number}             [options.transitionDuration = 500]                                       - The duration time (in milliseconds) for the transition between carousel items.
   * @param {?string}            [options.playText = Play]                                                - The text to use for the play button.
   * @param {?string}            [options.pauseText = Pause]                                              - The text to use for the pause button.
   * @param {?string}            [options.prefix = graupl-]                                               - The prefix used for CSS custom properties and attributes.
   * @param {?string}            [options.key = null]                                                     - The key used to generate IDs throughout the carousel.
   * @param {?(string|string[])} [options.initializeClass = initializing]                                 - The class(es) to apply when the carousel is initializing.
   * @param {boolean}            [options.initialize = false]                                             - A flag to initialize the carousel immediately upon creation.
   */
  constructor({
    carouselElement,
    carouselItemSelector = ".carousel-item",
    carouselItemContainerSelector = ".carousel-item-container",
    carouselControlSelector = ".carousel-control",
    carouselControlContainerSelector = ".carousel-control-container",
    carouselTabSelector = ".carousel-tab",
    carouselTabContainerSelector = ".carousel-tab-container",
    autoplaySelector = ".autoplay",
    nextSelector = ".next",
    previousSelector = ".previous",
    activeClass = "active",
    previousClass = "previous",
    nextClass = "next",
    playClass = "play",
    pauseClass = "pause",
    autoplay = true,
    transitionDelay = 10000,
    transitionDuration = 500,
    playText = "Play",
    pauseText = "Pause",
    prefix = "graupl-",
    key = null,
    initializeClass = "initializing",
    initialize = false,
  }) {
    super({
      prefix,
      key,
      initializeClass,
    });

    // Set DOM elements.
    this._dom.carousel = carouselElement;

    // Set query selectors.
    this._selectors.carouselItems = carouselItemSelector;
    this._selectors.carouselItemContainer = carouselItemContainerSelector;
    this._selectors.carouselControls = carouselControlSelector;
    this._selectors.carouselControlContainer = carouselControlContainerSelector;
    this._selectors.carouselTabs = carouselTabSelector;
    this._selectors.carouselTabContainer = carouselTabContainerSelector;
    this._selectors.autoplay = autoplaySelector;
    this._selectors.next = nextSelector;
    this._selectors.previous = previousSelector;

    // Set class names.
    this._classes.active = activeClass || "";
    this._classes.previous = previousClass || "";
    this._classes.next = nextClass || "";
    this._classes.play = playClass || "";
    this._classes.pause = pauseClass || "";

    // Set flags.
    this._autoplay = autoplay;

    // Set transition options.
    this._delays.transition = transitionDelay;
    this._durations.transition = transitionDuration;

    // Set labels.
    this._playText = playText || "";
    this._pauseText = pauseText || "";

    if (initialize) {
      this.initialize();
    }
  }

  /**
   * Initializes the carousel.
   */
  initialize() {
    try {
      // Add the initialization class.
      addClass(this._classes.initialize, this.dom.carousel);

      super.initialize();

      // Set up the DOM.
      this._setDOMElements();
      this._setIds();
      this._setAriaAttributes();
      this._setCustomProps();

      // Activate the first item.
      this.activateFirstItem();

      // Handle events.
      this._handleAutoplay();
      this._handleFocus();
      this._handleClick();
      this._handleHover();
      this._handleKeydown();
      this._handleKeyup();

      // Store the component.
      this._store();
    } catch (error) {
      console.error(error);
    } finally {
      // Remove the initialization class.
      requestAnimationFrame(() => {
        removeClass(this.initializeClass, this.dom.carousel);
      });
    }
  }

  /**
   * The class(es) to apply when a carousel item is active.
   *
   * @type {string|string[]}
   *
   * @see _classes.active
   */
  get activeClass() {
    return this._classes.active;
  }

  set activeClass(value) {
    isValidClassList({ activeClass: value });

    if (this._classes.active !== value) {
      this._classes.active = value;
    }
  }

  /**
   * The class(es) to apply to a carousel item that is the next active item.
   *
   * @type {string|string[]}
   *
   * @see _classes.previous
   */
  get previousClass() {
    return this._classes.previous;
  }

  set previousClass(value) {
    isValidClassList({ previousClass: value });

    if (this._classes.previous !== value) {
      this._classes.previous = value;
    }
  }

  /**
   * The class(es) to apply to a carousel item that is the next active item.
   *
   * @type {string|string[]}
   *
   * @see _classes.next
   */
  get nextClass() {
    return this._classes.next;
  }

  set nextClass(value) {
    isValidClassList({ nextClass: value });

    if (this._classes.next !== value) {
      this._classes.next = value;
    }
  }

  /**
   * The class(es) to apply to the autoplay button when the carousel is paused.
   *
   * @type {string|string[]}
   *
   * @see _classes.play
   */
  get playClass() {
    return this._classes.play;
  }

  set playClass(value) {
    isValidClassList({ playClass: value });

    if (this._classes.play !== value) {
      this._classes.play = value;
    }
  }

  /**
   * The class(es) to apply to the autoplay button when the carousel is playing.
   *
   * @type {string|string[]}
   *
   * @see _classes.pause
   */
  get pauseClass() {
    return this._classes.pause;
  }

  set pauseClass(value) {
    isValidClassList({ pauseClass: value });

    if (this._classes.pause !== value) {
      this._classes.pause = value;
    }
  }

  /**
   * The index of the currently active carousel item.
   *
   * @type {number}
   *
   * @see _currentItem
   */
  get currentItem() {
    return this._currentItem;
  }

  set currentItem(value) {
    isValidType("number", { currentItem: value });

    if (value === this.currentItem) {
      return;
    }

    if (value < 0) {
      this._currentItem = 0;
    } else if (value >= this.dom.carouselItems.length) {
      this._currentItem = this.dom.carouselItems.length - 1;
    } else {
      this._currentItem = value;
    }

    // Keep the aria selected in sync with the current item.
    const tabs = this._dom.carousel.querySelectorAll(
      this.selectors.carouselTab
    );
    if (tabs) {
      this.dom.carouselItems.forEach((item, index) => {
        item.setAttribute("aria-selected", index === this._currentItem);
      });
    }
  }

  /**
   * The currently active carousel item.
   *
   * @readonly
   *
   * @type {HTMLElement}
   */
  get currentCarouselItem() {
    return this.dom.carouselItems[this.currentItem];
  }

  /**
   * The currently active carousel tab.
   *
   * @readonly
   *
   * @type {HTMLElement}
   */
  get currentCarouselTab() {
    return this.dom.carouselTabs[this.currentItem];
  }

  /**
   * A flag to indicate if the carousel is currently playing.
   *
   * @type {boolean}
   *
   * @see _autoplay
   */
  get autoplay() {
    return this._autoplay;
  }

  set autoplay(value) {
    isValidType("boolean", { autoplay: value });

    if (this._autoplay !== value) {
      this._autoplay = value;
    }
  }

  /**
   * The delay in milliseconds before transitioning slides.
   *
   * @type {number}
   *
   * @see _delays.transition
   */
  get transitionDelay() {
    return this._delays.transition;
  }

  set transitionDelay(value) {
    isValidType("number", { transitionDelay: value });

    if (value !== this.transitionDelay && value >= 0) {
      this._delays.transition = value;
    }
  }

  /**
   * The duration time (in milliseconds) for the transition between carousel items.
   *
   * @type {number}
   *
   * @see _durations.transition
   */
  get transitionDuration() {
    return this._durations.transition;
  }

  set transitionDuration(value) {
    isValidType("number", { transitionDuration: value });

    if (this._durations.transition !== value && value >= 0) {
      this._durations.transition = value;
      this._setTransitionDuration();
    }
  }

  /**
   * The label for the autoplay button when the carousel is paused.
   *
   * @type {string}
   *
   * @see _playText
   */
  get playText() {
    return this._playText;
  }

  set playText(value) {
    isValidType("string", { playText: value });

    if (this._playText !== value) {
      this._playText = value;
    }
  }

  /**
   * The label for the autoplay button when the carousel is playing.
   *
   * @type {string}
   *
   * @see _pauseText
   */
  get pauseText() {
    return this._pauseText;
  }

  set pauseText(value) {
    isValidType("string", { pauseText: value });

    if (this._pauseText !== value) {
      this._pauseText = value;
    }
  }

  /**
   * The current action being performed by the carousel.
   *
   * @type {string}
   *
   * @see _currentAction
   */
  get currentAction() {
    return this._currentAction;
  }

  /**
   * Validates all aspects of the carousel to ensure proper functionality.
   *
   * @protected
   *
   * @return {boolean} - The results of the validation.
   */
  _validate() {
    super._validate();

    // Boolean checks.
    const booleans = {
      autoplay: this._autoplay,
    };

    // Check the booleans.
    const booleanChecks = isValidType("boolean", booleans);

    // Handle boolean check failure.
    if (!booleanChecks) {
      this._errors.push(booleanChecks.message);
      this._valid = false;
    }

    // String checks.
    const strings = {
      playText: this._playText,
      pauseText: this._pauseText,
    };

    // Check the strings.
    const stringChecks = isValidType("string", strings);

    // Handle string check failures.
    if (!stringChecks) {
      this._errors.push(stringChecks.message);
      this._valid = false;
    }

    return this._valid;
  }

  /**
   * Sets all DOM elements within the carousel.
   *
   * Utilizes _setDOMElementType and _resetDOMElementType.
   *
   * @protected
   */
  _setDOMElements() {
    this._setDOMElementType("carouselItemContainer", {
      context: this.dom.carousel,
    });
    this._setDOMElementType("carouselControlContainer", {
      context: this.dom.carousel,
    });
    this._setDOMElementType("carouselTabContainer", {
      context: this.dom.carousel,
    });

    if (this.dom.carouselItemContainer) {
      this._setDOMElementType("carouselItems", {
        context: this.dom.carouselItemContainer,
      });
    }
    if (this.dom.carouselControlContainer) {
      this._setDOMElementType("carouselControls", {
        context: this.dom.carouselControlContainer,
      });
      this._setDOMElementType("autoplay", {
        context: this.dom.carouselControlContainer,
      });
      this._setDOMElementType("next", {
        context: this.dom.carouselControlContainer,
      });
      this._setDOMElementType("previous", {
        context: this.dom.carouselControlContainer,
      });
    }

    if (this._dom.carouselTabContainer) {
      this._setDOMElementType("carouselTabs", {
        context: this.dom.carouselTabContainer,
      });
    }
  }

  /**
   * Sets the IDs of the carousel and it's children if they do not already exist.
   *
   * The generated IDs use the key and follow the format:
   *  - carousel: `carousel-${key}`
   *  - carousel items: `carousel-item-${key}-${index}`
   *  - carousel tabs: `carousel-tab-${key}-${index}`
   */
  _setIds() {
    this.dom.carousel.id = this.dom.carousel.id || `carousel-${this.key}`;

    this.dom.carouselItems.forEach((item, index) => {
      item.id = item.id || `carousel-item-${this.key}-${index}`;
    });

    this.dom.carouselTabs.forEach((tab, index) => {
      tab.id = tab.id || `carousel-tab-${this.key}-${index}`;
    });

    this._id = this.dom.carousel.id;
  }

  /**
   * Sets ARIA attributes throughout the carousel.
   */
  _setAriaAttributes() {
    // Make sure the carousel has a proper role.
    // Sections and role="region" are acceptable in certain cases, so
    // we only need to fallback to role="group" if neither of those are present.
    if (
      !isTag("section", { carousel: this.dom.carousel }) &&
      !this.dom.carousel.getAttribute("role") !== "region"
    ) {
      this.dom.carousel.setAttribute("role", "group");
    }

    // Set the role description for the carousel.
    this._dom.carousel.setAttribute("aria-roledescription", "carousel");

    if (this.dom.carouselTabContainer) {
      this.dom.carouselTabContainer.setAttribute("role", "tablist");
    }

    this.dom.carouselTabs.forEach((tab, index) => {
      if (!isTag("button", { tab: tab })) {
        tab.setAttribute("role", "button");
      }

      tab.setAttribute("aria-selected", index === 0);
      tab.setAttribute("aria-controls", this.dom.carouselItems[index].id);
    });
  }

  /**
   * Sets the transition duration for the carousel as a CSS custom property.
   *
   * The custom property is set as `--graupl-carousel-transition-duration`.
   *
   * @protected
   */
  _setCustomProps() {
    this.dom.carousel.style.setProperty(
      `--${this.prefix}carousel-transition-duration`,
      `${this.transitionDuration}ms`
    );
  }

  /**
   * Handles the autoplay functionality of the carousel.
   *
   * - Adds the appropriate class to the autoplay button.
   * - Removes the appropriate class from the autoplay button.
   * - Sets the appropriate aria-label for the autoplay button.
   * - Sets the appropriate aria-live for the carousel.
   * - Sets/clears the interval for autoplaying the carousel.
   *
   * @protected
   */
  _handleAutoplay() {
    if (this.autoplay) {
      addClass(this.pauseClass, this.dom.autoplay);
      removeClass(this.playClass, this.dom.autoplay);

      this.dom.autoplay.setAttribute("aria-label", this.pauseText);
      this.dom.carousel.setAttribute("aria-live", "off");

      this._setInterval(() => this.activateNextItem(), this.transitionDelay);
    } else {
      addClass(this.playClass, this.dom.autoplay);
      removeClass(this.pauseClass, this.dom.autoplay);

      this.dom.autoplay.setAttribute("aria-label", this.playText);
      this.dom.carousel.setAttribute("aria-live", "polite");

      this._clearInterval();
    }
  }

  /**
   * Handles the focus events throughout the carousel for proper use.
   *
   * - Adds a `focusin` listener to the carousel element to pause autoplay.
   * - Adds a `focusout` listener to the carousel element to resume autoplay.
   */
  _handleFocus() {
    // Pause autoplay when anything in the carousel is focused.
    this._addEventListener("focusin", this.dom.carousel, () => {
      if (this.autoplay) {
        this._clearInterval();
      }
    });

    this._addEventListener("focusout", this.dom.carousel, () => {
      if (this.autoplay) {
        this._setInterval(() => this.activateNextItem(), this.transitionDelay);
      }
    });
  }

  /**
   * Handles the click events throughout the carousel.
   *
   * - Adds a `pointerup` listener to the next control to activate the next item.
   * - Adds a `pointerup` listener to the previous control to activate the previous item.
   * - Adds a `pointerup` listener to the autoplay control to toggle autoplay.
   * - Adds a `pointerup` listener to each tab control to activate the corresponding item.
   */
  _handleClick() {
    this._addEventListener("pointerup", this.dom.next, () => {
      this.activateNextItem();
    });

    this._addEventListener("pointerup", this.dom.previous, () => {
      this.activatePreviousItem();
    });

    this._addEventListener("pointerup", this.dom.autoplay, () => {
      this.toggleAutoplay();
    });

    this.dom.carouselTabs.forEach((tab, index) => {
      this._addEventListener("pointerup", tab, () => {
        if (this.currentItem > index) {
          this._currentAction = "previous";
        } else {
          this._currentAction = "next";
        }

        this.activateItem(index);
      });
    });
  }

  /**
   * Handles the hover events throughout the carousel for proper use.
   *
   * - Adds a `pointerover` listener to the carousel to pause autoplay.
   * - Adds a `pointerleave` listener to the carousel to resume autoplay.
   */
  _handleHover() {
    // Pause autoplay when anything in the carousel is hovered.
    this._addEventListener("pointerover", this.dom.carousel, () => {
      if (this.autoplay) {
        this._clearInterval();
      }
    });

    this._addEventListener("pointerleave", this.dom.carousel, () => {
      if (this.autoplay) {
        this._setInterval(() => this.activateNextItem(), this.transitionDelay);
      }
    });
  }

  /**
   * Handles keydown events throughout the carousel item for proper use.
   *
   * - Adds a `keydown` listener to all control elements.
   *   - Prevents Space and Enter key events.
   * - Adds a `keydown` listener to all tab elements.
   *   - Prevents Space and Enter key events.
   */
  _handleKeydown() {
    this.dom.carouselControls.forEach((control) => {
      this._addEventListener("keydown", control, (event) => {
        const key = keyPress(event);

        switch (key) {
          case "Space":
          case "Enter":
            // Prevent the default action of the event.
            preventEvent(event);

            break;
        }
      });
    });

    this.dom.carouselTabs.forEach((tab) => {
      this._addEventListener("keydown", tab, (event) => {
        const key = keyPress(event);

        switch (key) {
          case "Space":
          case "Enter":
            // Prevent the default action of the event.
            preventEvent(event);

            break;
        }
      });
    });
  }

  /**
   * Handles keyup events throughout the carousel item for proper use.
   *
   * - Adds a `keyup` listener to all control elements.
   *   - Activates the next/previous item depending on which control is pressed.
   *   - Toggles autoplay if the autoplay control is pressed.
   *
   */
  _handleKeyup() {
    // Activate the next item if the space or enter key on the next control.
    this._addEventListener("keyup", this.dom.next, (event) => {
      const key = keyPress(event);

      switch (key) {
        case "Space":
        case "Enter":
          this.activateNextItem();

          // Prevent the default action of the event.
          preventEvent(event);

          break;
      }
    });

    // Activate the previous item if the space or enter key on the previous control.
    this._addEventListener("keyup", this.dom.previous, (event) => {
      const key = keyPress(event);

      switch (key) {
        case "Space":
        case "Enter":
          this.activatePreviousItem();

          // Prevent the default action of the event.
          preventEvent(event);

          break;
      }
    });

    // Toggle autoplay if the space or enter key on the autoplay control.
    this._addEventListener("keyup", this.dom.autoplay, (event) => {
      const key = keyPress(event);

      switch (key) {
        case "Space":
        case "Enter":
          this.toggleAutoplay();

          // Prevent the default action of the event.
          preventEvent(event);

          break;
      }
    });

    // Activate the item if the space or enter key on the tab control.
    this.dom.carouselTabs.forEach((tab, index) => {
      this._addEventListener("keyup", tab, (event) => {
        const key = keyPress(event);

        switch (key) {
          case "Space":
          case "Enter":
            this.activateItem(index);

            // Prevent the default action of the event.
            preventEvent(event);

            break;
        }
      });
    });
  }

  /**
   * Activates the current carousel item.
   */
  activateCurrentItem() {
    addClass(this.activeClass, this.currentCarouselItem);

    if (this.currentCarouselTab) {
      this.currentCarouselTab.setAttribute("aria-selected", true);
      addClass(this.activeClass, this.currentCarouselTab);
    }
  }

  /**
   * Deactivates the current carousel item.
   */
  deactivateCurrentItem() {
    removeClass(this.activeClass, this.currentCarouselItem);

    if (this.currentCarouselTab) {
      this.currentCarouselTab.setAttribute("aria-selected", false);
      removeClass(this.activeClass, this.currentCarouselTab);
    }
  }

  /**
   * Activates the carousel item at a given index.
   *
   * @param {number} index - The index of the carousel item to activate.
   */
  activateItem(index) {
    const currentIndex = this.currentItem;

    this.dom.carousel.dataset.grauplAction = this._currentAction;

    if (this.autoplay) {
      this._clearInterval();
    }

    addClass(this.previousClass, this.currentCarouselItem);
    addClass(this.nextClass, this.dom.carouselItems[index]);

    requestAnimationFrame(() => {
      this.deactivateCurrentItem();
      this.currentItem = index;
      this.activateCurrentItem();

      requestAnimationFrame(() => {
        setTimeout(() => {
          removeClass(this.previousClass, this.dom.carouselItems[currentIndex]);
          removeClass(this.nextClass, this.currentCarouselItem);
        }, this.transitionDuration);
      });
    });

    if (this.autoplay) {
      this._setInterval(() => this.activateNextItem(), this.transitionDelay);
    }
  }

  /**
   * Activates the first carousel item.
   */
  activateFirstItem() {
    this.activateItem(0);
  }

  /**
   * Activates the last carousel item.
   */
  activateLastItem() {
    this.activateItem(this.dom.carouselItems.length - 1);
  }

  /**
   * Activates the next carousel item.
   */
  activateNextItem() {
    this._currentAction = "next";

    if (this.currentItem + 1 >= this.dom.carouselItems.length) {
      this.activateFirstItem();
    } else {
      this.activateItem(this.currentItem + 1);
    }
  }

  /**
   * Activates the previous carousel item.
   */
  activatePreviousItem() {
    this._currentAction = "previous";

    if (this.currentItem - 1 < 0) {
      this.activateLastItem();
    } else {
      this.activateItem(this.currentItem - 1);
    }
  }

  /**
   * Toggles autoplay on the carousel.
   */
  toggleAutoplay() {
    this.autoplay = !this.autoplay;

    this._handleAutoplay();
  }
}

export default Carousel;
